/**
 * Which required pieces of a persisted Locate skill are not yet populated.
 * Used to validate `RunLocateSkill` steps (caution + block execution) and to
 * render per-row cautions on the skill "parameters" page. Reads defensively
 * from the permissive `SkillData.shared` / `methodConfig` records.
 */

export type MissingLocateSkillConfig = {
  capturePose: boolean;
  /**
   * Derived from the method-specific `methodConfig` the runtime consumes — NOT
   * `snapshotStorageID`, which is display-only and absent on legacy skills and
   * failed snapshot captures, so it would falsely flag runnable skills.
   */
  teach: boolean;
  referencePlane: boolean;
};

type LocateSkillLike = {
  shared?: Record<string, unknown> | null;
  method?: string | null;
  methodConfig?: Record<string, unknown> | null;
};

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Methods that need no taught reference (`blob`, `mock`) and unmodelled methods
 * return `false`, so a runnable skill is never falsely flagged.
 */
function isTeachMissing(
  method: string | null | undefined,
  methodConfig: Record<string, unknown> | null | undefined,
): boolean {
  const mc = (methodConfig ?? {}) as Record<string, unknown>;

  switch (method) {
    case 'dino_fast': {
      const { positives } = mc;

      return !(Array.isArray(positives) && positives.length > 0);
    }
    case 'text_prompt':
      return !isNonEmptyString(mc.textInput);
    case 'shape_match': {
      const templateImage = mc.templateImage as
        | { storageID?: unknown }
        | undefined;

      return !(templateImage && isNonEmptyString(templateImage.storageID));
    }
    case 'custom_checkpoint':
      // A Roboflow-hosted model is identified by `roboflowId` (the model url)
      // rather than a runner-hosted checkpoint `modelPath`.
      return mc.modelSource === 'roboflow'
        ? !isNonEmptyString(mc.roboflowId)
        : !isNonEmptyString(mc.modelPath);
    default:
      return false;
  }
}

export function getMissingLocateSkillConfig(
  skill: LocateSkillLike | null | undefined,
): MissingLocateSkillConfig {
  const shared = (skill?.shared ?? {}) as Record<string, unknown>;

  const capturePose =
    shared.capturePose == null &&
    !isNonEmptyString(shared.capturePoseSpaceItemId);

  const teach = isTeachMissing(skill?.method, skill?.methodConfig);

  const projection = (shared.projection ?? {}) as Record<string, unknown>;

  // An inline plane is usable only once all three axes are captured.
  const inlinePlane = projection.plane as
    | { origin?: unknown; xAxis?: unknown; yAxis?: unknown }
    | null
    | undefined;

  const inlinePlaneComplete = Boolean(
    inlinePlane?.origin && inlinePlane?.xAxis && inlinePlane?.yAxis,
  );

  // `plane` is the default method when none is stored.
  const referencePlane =
    projection.method === 'detection3d'
      ? projection.detection3d == null
      : !inlinePlaneComplete && !isNonEmptyString(projection.planeSpaceItemId);

  return { capturePose, teach, referencePlane };
}
